import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, apiConfig } from '../config/authConfig';
import { logger } from '../utils/logger';

// Create MSAL instance with a safe test stub fallback
const createMsalInstance = (): PublicClientApplication => {
  const isTestEnv = process.env.NODE_ENV === 'test';

  if (isTestEnv) {
    const testAccount = {
      homeAccountId: 'test-account',
      environment: 'test',
      tenantId: 'test-tenant',
      username: 'test@example.com',
      localAccountId: 'test-account'
    } as const;

    const fakeResponse = {
      accessToken: 'test-token',
      idToken: 'test-token',
      account: testAccount
    };

    return {
      initialize: async () => undefined,
      getAllAccounts: () => [testAccount],
      setActiveAccount: () => undefined,
      loginPopup: async () => fakeResponse,
      acquireTokenPopup: async () => fakeResponse,
      acquireTokenSilent: async () => fakeResponse
    } as unknown as PublicClientApplication;
  }

  try {
    return new PublicClientApplication(msalConfig);
  } catch (error) {
    // In non-test environments we still want visibility into configuration issues
    logger.error('Failed to initialize MSAL instance', error);
    throw error;
  }
};

const msalInstance = createMsalInstance();

export class ApiError extends Error {
  constructor(
    public message: string,
    public status?: number,
    public code?: string,
    public correlationId?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class AuthError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export interface ShopifyProduct {
  id: string;
  title: string;
  price: number;
  images?: Array<{ src: string }>;
  vendor?: string;
}

export interface ShopifyStats {
  total_products: number;
  total_revenue: number;
  conversion_rate?: number;
  average_order_value?: number;
}

export interface AnalyzeRequest {
  images: string[];
}

export interface AnalyzeResponse<T = Record<string, unknown>> {
  status: string;
  analyzedMetadata: T;
  correlationId: string;
}

const API_BASE_URL = apiConfig.baseUrl;

async function getAuthToken(forceInteractive = false): Promise<string> {
  try {
    const scopes = apiConfig.scopes;
    let accounts = msalInstance.getAllAccounts();

    const interactiveAcquire = async (): Promise<string> => {
      accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) {
        const loginResponse = await msalInstance.loginPopup({
          scopes,
          prompt: 'select_account',
        });
        if (loginResponse.account) {
          msalInstance.setActiveAccount(loginResponse.account);
        }
        return loginResponse.idToken || loginResponse.accessToken;
      }

      const popupResponse = await msalInstance.acquireTokenPopup({
        scopes,
        account: accounts[0],
        prompt: 'select_account',
      });
      if (popupResponse.account) {
        msalInstance.setActiveAccount(popupResponse.account);
      }
      return popupResponse.idToken || popupResponse.accessToken;
    };

    if (forceInteractive) {
      return interactiveAcquire();
    }

    if (accounts.length === 0) {
      return interactiveAcquire();
    }

    const silentRequest = {
      scopes,
      account: accounts[0]
    };

    try {
      const response = await msalInstance.acquireTokenSilent(silentRequest);
      return response.idToken || response.accessToken;
    } catch (silentError) {
      const errorCode = (silentError as { errorCode?: string })?.errorCode;
      const requiresInteraction =
        silentError instanceof InteractionRequiredAuthError ||
        errorCode === 'monitor_window_timeout' ||
        errorCode === 'block_token_requests' ||
        errorCode === 'token_renewal_error';

      if (requiresInteraction) {
        return interactiveAcquire();
      }

      throw silentError;
    }
  } catch (error) {
    logger.warn('Failed to get auth token:', error);
    throw new AuthError('Authentifizierung fehlgeschlagen. Bitte laden Sie die Seite neu oder melden Sie sich erneut an.');
  }
}

async function apiRequest<T>(
  endpoint: string, 
  options: RequestInit = {},
  retryOnAuth = true
): Promise<T> {
  logger.debug(`API Request: ${options.method || 'GET'} ${endpoint}`);
  
  const token = await getAuthToken();
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>)
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers
    });
  } catch (networkError) {
    logger.error('Network request failed', networkError);
    throw new ApiError('Netzwerkfehler: Der Server ist nicht erreichbar. Bitte prüfen Sie Ihre Internetverbindung.');
  }

  if (!response.ok) {
    if (response.status === 401 && retryOnAuth) {
      logger.warn('API request unauthorized, triggering interactive login retry.');
      await getAuthToken(true);
      return apiRequest<T>(endpoint, options, false);
    }

    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    let errorCode = 'UNKNOWN_ERROR';
    let correlationId = response.headers.get('x-correlation-id') || undefined;

    try {
      const errorBody = await response.json();
      if (errorBody.error) {
        errorMessage = errorBody.error;
      }
      if (errorBody.code) {
        errorCode = errorBody.code;
      }
      if (errorBody.correlationId) {
        correlationId = errorBody.correlationId;
      }
    } catch {
      // Could not parse JSON error body, stick to status text
    }

    logger.error(`API Error ${response.status}: ${errorMessage}`, { status: response.status, code: errorCode, correlationId });
    
    // User-friendly mapping for common status codes
    if (response.status === 401) {
      throw new AuthError('Sitzung abgelaufen. Bitte melden Sie sich erneut an.');
    } else if (response.status === 403) {
      throw new ApiError('Zugriff verweigert. Sie haben keine Berechtigung für diese Aktion.', response.status, errorCode, correlationId);
    } else if (response.status === 500) {
      throw new ApiError('Serverfehler. Bitte versuchen Sie es später erneut.', response.status, errorCode, correlationId);
    }

    throw new ApiError(errorMessage, response.status, errorCode, correlationId);
  }

  return response.json();
}

export async function getShopifyProducts(search?: string, limit = 50): Promise<ShopifyProduct[]> {
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (limit) params.append('limit', limit.toString());
  
  const queryString = params.toString();
  const endpoint = `/shopify/products${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiRequest<{ products: ShopifyProduct[] }>(endpoint);
  return response.products || [];
}

export async function getShopifyStats(): Promise<ShopifyStats> {
  return apiRequest<ShopifyStats>('/shopify/stats');
}

export async function analyzeImages(request: AnalyzeRequest): Promise<AnalyzeResponse> {
  return apiRequest<AnalyzeResponse>('/analyze', {
    method: 'POST',
    body: JSON.stringify(request)
  });
}

export async function uploadAndPublish(metadata: Record<string, unknown>): Promise<{ message: string }> {
  return apiRequest('/upload', {
    method: 'POST',
    body: JSON.stringify(metadata)
  });
}

export async function getBrandingGuidelines(): Promise<{ guidelines: string; lastUpdated: string }> {
  return apiRequest('/branding/guidelines');
}

export async function saveBrandingGuidelines(guidelines: string): Promise<{ message: string }> {
  return apiRequest('/branding/guidelines', {
    method: 'POST',
    body: JSON.stringify({ guidelines })
  });
}

export async function refineProduct(data: Record<string, unknown>): Promise<{ refinedData: Record<string, unknown> }> {
  return apiRequest('/refine', {
    method: 'POST',
    body: JSON.stringify(data)
  });
}